import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUploadCloud, FiShare2, FiUsers, FiGlobe, FiChevronLeft, FiChevronDown, FiX, FiRefreshCw, FiCheck } from 'react-icons/fi';
import { upsertMyTool, installAgentForAgency, submitPublishRequest, summarizeAgentAgencyInstall, findAgentTool, type AgentAgencyInstallSummary, type AgentToolRef } from '../services/myToolsApi';
import { shareAgent, revokeAgentShare, regenerateAgentShare, refreshAgentShare, listAgentShares } from '../features/claudeagents/claudeAgentApi';
import { ROUTES } from '../app/routes';

interface AgentShareMenuModalProps {
  agentId: string;
  agentName: string;
  isAgencyOwner: boolean;
  onClose: () => void;
  /** Present only when opened from an existing My Creations row (as opposed to
   * Digital Workforce's own Share button, which has no listing yet). Switches
   * "Add to Marketplace" to "Publish" (acts on this row directly, no navigation)
   * and adds a "Share with Agency" option; Install for Agency skips minting a
   * private row first since one already exists. */
  existingTool?: { id: string; visibility: 'just_me' | 'my_agency' | 'published'; publishStatus?: 'pending' | 'approved' | 'rejected' | 'changes_requested'; isAgencyInstalled?: boolean };
  onShareWithAgency?: () => void;
  /** Present only when existingTool.isAgencyInstalled — swaps the Install-for-Agency
   * row for an Uninstall one, same as the old kebab menu's Install/Uninstall toggle. */
  onUninstallForAgency?: () => void;
}

// "Add to Marketplace" (Digital Workforce's Share, no listing yet) is hidden;
// set to true to show it again. Publish on an existing My Creations row stays.
const SHOW_ADD_TO_MARKETPLACE = false;

// Agent-level counterpart to SkillShareMenuModal — same three/four-option shape
// (Publish / [Share with Agency] / Share via Link / Install for Agency) for UX
// consistency, but driven by agent-share endpoints (Python) rather than
// skill_shares (.NET). Each share mints a frozen snapshot server-side
// (system prompt + skills-with-content + MCP requirements) — editing the
// live agent afterward never affects an already-shared copy.
const AgentShareMenuModal: React.FC<AgentShareMenuModalProps> = ({ agentId, agentName, isAgencyOwner, onClose, existingTool, onShareWithAgency, onUninstallForAgency }) => {
  const navigate = useNavigate();
  const [view, setView] = useState<'menu' | 'link'>('menu');
  const [error, setError] = useState('');

  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);

  const [scope, setScope] = useState<'agency' | 'anyone'>('agency');
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  const [creatingLink, setCreatingLink] = useState(false);
  const [linkResult, setLinkResult] = useState<{ id: number; token: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [linkBusy, setLinkBusy] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  // The my_tools row already backing this agent, when opened from Digital Workforce
  // (no existingTool). Install for Agency reuses it rather than minting another.
  const [agentTool, setAgentTool] = useState<AgentToolRef | null>(null);

  // Load any share already active for this agent, so re-opening "Share via Link"
  // shows the existing link (and lets it be copied/refreshed/revoked) instead of
  // only ever offering to mint a brand-new one on top of it.
  useEffect(() => {
    let cancelled = false;
    listAgentShares(agentId)
      .then(async shares => {
        if (cancelled) return;
        const existing = shares[shares.length - 1];
        if (existing) {
          setLinkResult({ id: existing.id, token: existing.token });
          setScope(existing.scope);
        }
        if (!existingTool) {
          const found = await findAgentTool(shares.map(s => s.token)).catch(() => null);
          if (!cancelled) setAgentTool(found);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingExisting(false); });
    return () => { cancelled = true; };
  }, [agentId, existingTool]);

  const [installingAgency, setInstallingAgency] = useState(false);
  const [agencyResults, setAgencyResults] = useState<AgentAgencyInstallSummary | null>(null);

  // Marketplace listings are authored in the My Tools wizard, not here — this just
  // hands off to it with this agent pre-selected, so metadata (icon, category,
  // description, visibility) gets a real form instead of publishing with placeholders.
  // Only used when there's no existing listing yet (opened from Digital Workforce).
  const handleAddToMarketplace = () => {
    onClose();
    navigate(ROUTES.MY_TOOLS + '?view=create', { state: { prefillAgent: { agentId } } });
  };

  // Used instead of handleAddToMarketplace when opened on an existing My Creations
  // row — the listing already exists, so this just submits it for review directly.
  const handlePublishExisting = async () => {
    if (!existingTool) return;
    setPublishing(true);
    setError('');
    try {
      await submitPublishRequest(existingTool.id);
      setPublished(true);
    } catch (e: any) {
      setError(e.message === 'already_pending' ? 'This tool already has a pending review request.' : (e.message || 'Failed to submit for review'));
    } finally {
      setPublishing(false);
    }
  };

  const shareUrl = (token: string) => `${window.location.origin}/share-agent/${token}`;

  const handleCreateLink = async () => {
    setCreatingLink(true);
    setError('');
    try {
      const share = await shareAgent(agentId, scope);
      setLinkResult({ id: share.id, token: share.token });
    } catch (e: any) {
      setError(e.message || 'Failed to create share link');
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopy = (token: string) => {
    navigator.clipboard?.writeText(shareUrl(token)).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    try {
      await revokeAgentShare(linkResult.id);
      setLinkResult(null);
    } catch (e: any) {
      setError(e.message || 'Failed to revoke share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleRegenerate = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    try {
      const regenerated = await regenerateAgentShare(linkResult.id);
      setLinkResult({ id: regenerated.id, token: regenerated.token });
    } catch (e: any) {
      setError(e.message || 'Failed to regenerate share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const [refreshed, setRefreshed] = useState(false);
  const handleRefresh = async () => {
    if (!linkResult) return;
    setLinkBusy(true);
    setError('');
    try {
      await refreshAgentShare(linkResult.id);
      setRefreshed(true);
      setTimeout(() => setRefreshed(false), 2500);
    } catch (e: any) {
      setError(e.message || 'Failed to refresh share link');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleInstallForAgency = async () => {
    setInstallingAgency(true);
    setError('');
    try {
      let toolId = existingTool?.id ?? agentTool?.id;
      if (!toolId) {
        // No listing yet (opened from Digital Workforce) — mint a private
        // (visibility: just_me) my_tools row backing this batch action — never
        // submitted for review, never shown in the marketplace. Just a place for
        // the .NET side to look up the share token and mint skill copies from.
        const share = await shareAgent(agentId, 'agency');
        toolId = await upsertMyTool({
          type: 1, // ToolType.Agent
          name: agentName.trim(),
          category: 'AI & ML',
          icon: '🤖',
          description: '',
          skills: '',
          kbFileNames: [],
          visibility: 0,
          agentShareToken: share.token,
        });
      }
      setAgencyResults(summarizeAgentAgencyInstall(await installAgentForAgency(toolId)));
    } catch (e: any) {
      setError(e.message || 'Failed to install for agency');
    } finally {
      setInstallingAgency(false);
    }
  };

  // Opened from Digital Workforce on an agent that's already installed for the agency
  // (in an earlier session) - show that instead of offering to install it again.
  const alreadyInstalledFromDw = !existingTool && !!agentTool?.isAgencyInstalled && !agencyResults;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      backdropFilter: 'blur(6px) saturate(1.2)', WebkitBackdropFilter: 'blur(6px) saturate(1.2)',
      zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{
        background: 'var(--surface-card, #1a1a1f)',
        backdropFilter: 'blur(24px) saturate(1.3)', WebkitBackdropFilter: 'blur(24px) saturate(1.3)',
        border: '1.5px solid var(--border-glass, rgba(255,255,255,0.1))', borderRadius: 18, padding: 24,
        width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 14,
        boxShadow: '0 32px 80px rgba(0,0,0,0.35)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {view === 'link' && (
            <button onClick={() => setView('menu')} style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              color: 'var(--text-secondary)', display: 'flex',
            }} aria-label="Back">
              <FiChevronLeft size={18} />
            </button>
          )}
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)', flex: 1 }}>
            {view === 'menu' ? `Share "${agentName}"` : 'Share via Link'}
          </h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)',
            display: 'flex', padding: 4,
          }} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>

        {error && <p style={{ color: '#f87171', fontSize: 12.5, margin: 0 }}>{error}</p>}

        {view === 'menu' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {existingTool ? (
              <button onClick={handlePublishExisting}
                disabled={publishing || published || existingTool.visibility === 'published' || existingTool.publishStatus === 'pending'}
                style={{ ...menuRowStyle, opacity: publishing ? 0.6 : 1 }}>
                {published || existingTool.publishStatus === 'pending' ? <FiCheck size={18} style={{ color: '#4ade80', flexShrink: 0 }} /> : <FiUploadCloud size={18} style={{ color: '#f97316', flexShrink: 0 }} />}
                <div style={{ textAlign: 'left' }}>
                  <div style={menuRowTitleStyle}>
                    {publishing ? 'Publishing…' : existingTool.visibility === 'published' ? 'Published' : (published || existingTool.publishStatus === 'pending') ? 'Submitted for review' : 'Publish'}
                  </div>
                  {!published && existingTool.visibility !== 'published' && existingTool.publishStatus !== 'pending' && (
                    <div style={menuRowSubStyle}>Submit this for a platform admin's approval</div>
                  )}
                </div>
              </button>
            ) : SHOW_ADD_TO_MARKETPLACE && (
              <button onClick={handleAddToMarketplace} style={menuRowStyle}>
                <FiUploadCloud size={18} style={{ color: '#f97316', flexShrink: 0 }} />
                <div style={{ textAlign: 'left' }}>
                  <div style={menuRowTitleStyle}>Add to Marketplace</div>
                  <div style={menuRowSubStyle}>Opens the listing form, with this agent already attached</div>
                </div>
              </button>
            )}

            {existingTool && onShareWithAgency && (
              <button onClick={() => { onShareWithAgency(); onClose(); }} disabled={existingTool.visibility !== 'just_me'} style={menuRowStyle}>
                <FiUsers size={18} style={{ color: '#4ade80', flexShrink: 0 }} />
                <div style={{ textAlign: 'left' }}>
                  <div style={menuRowTitleStyle}>{existingTool.visibility === 'just_me' ? 'Share with Agency' : 'Shared with your agency'}</div>
                  {existingTool.visibility === 'just_me' && <div style={menuRowSubStyle}>Makes this visible in your agency's marketplace</div>}
                </div>
              </button>
            )}

            <button onClick={() => setView('link')} style={menuRowStyle}>
              <FiShare2 size={18} style={{ color: '#60a5fa', flexShrink: 0 }} />
              <div style={{ textAlign: 'left' }}>
                <div style={menuRowTitleStyle}>Share via Link</div>
                <div style={menuRowSubStyle}>Anyone with the link installs their own independent copy</div>
              </div>
            </button>

            {isAgencyOwner && existingTool?.isAgencyInstalled && (
              <button onClick={() => { onUninstallForAgency?.(); onClose(); }} style={menuRowStyle}
                title="Remove this agent from every user in your agency">
                <FiUsers size={18} style={{ color: '#f87171', flexShrink: 0 }} />
                <div style={{ textAlign: 'left' }}>
                  <div style={menuRowTitleStyle}>Uninstall for Agency</div>
                </div>
              </button>
            )}
            {isAgencyOwner && alreadyInstalledFromDw && (
              <div style={{ ...menuRowStyle, cursor: 'default' }}>
                <FiCheck size={18} style={{ color: '#4ade80', flexShrink: 0 }} />
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={menuRowTitleStyle}>Installed for your agency</div>
                  <div style={menuRowSubStyle}>Teammates who join later get it automatically at their next sign-in</div>
                </div>
              </div>
            )}
            {isAgencyOwner && !existingTool?.isAgencyInstalled && !alreadyInstalledFromDw && (
              <button onClick={handleInstallForAgency} disabled={loadingExisting || installingAgency || !!agencyResults} style={{ ...menuRowStyle, opacity: loadingExisting || installingAgency ? 0.6 : 1 }}>
                {agencyResults ? <FiCheck size={18} style={{ color: '#4ade80', flexShrink: 0 }} /> : <FiUsers size={18} style={{ color: '#4ade80', flexShrink: 0 }} />}
                <div style={{ textAlign: 'left', flex: 1 }}>
                  <div style={menuRowTitleStyle}>
                    {installingAgency ? 'Installing for agency…' : agencyResults ? 'Installed for your agency' : 'Install for Agency'}
                  </div>
                  {!agencyResults && <div style={menuRowSubStyle}>Creates a brand-new agent for every active user in your agency</div>}
                  {agencyResults && (
                    <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={menuRowSubStyle}>{agencyResults.created} agent{agencyResults.created === 1 ? '' : 's'} created</div>
                      {agencyResults.alreadyInstalled > 0 && (
                        <div style={menuRowSubStyle}>{agencyResults.alreadyInstalled} already had it</div>
                      )}
                      {agencyResults.pendingSetup > 0 && (
                        <div style={menuRowSubStyle}>
                          {agencyResults.pendingSetup} teammate{agencyResults.pendingSetup === 1 ? " isn't" : "s aren't"} set up yet - that happens automatically at their next sign-in, and they'll get it then
                        </div>
                      )}
                      {agencyResults.needsMcpSetup > 0 && (
                        <div style={{ ...menuRowSubStyle, color: '#fbbf24' }}>{agencyResults.needsMcpSetup} still need MCP setup individually</div>
                      )}
                      {agencyResults.failed > 0 && (
                        <div style={{ ...menuRowSubStyle, color: '#f87171' }}>{agencyResults.failed} couldn't be created</div>
                      )}
                    </div>
                  )}
                </div>
              </button>
            )}
          </div>
        )}

        {view === 'link' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              Anyone who opens the link gets their own brand-new agent — a fresh copy of the system
              prompt and skills, never a reference to yours. MCPs the recipient already has credentials
              for are attached automatically; anything else is left for them to set up.
            </p>

            {loadingExisting && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 12.5, margin: 0 }}>Checking for an existing link…</p>
            )}

            {!loadingExisting && !linkResult && (
              <>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>General access</div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                      background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)',
                    }}>
                      {scope === 'anyone' ? <FiGlobe size={17} /> : <FiUsers size={17} />}
                    </div>
                    <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
                      <button type="button" onClick={() => setScopeMenuOpen(o => !o)} style={dropdownBtnStyle}>
                        <span style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-primary)' }}>
                          {scope === 'anyone' ? 'Anyone with the link' : 'Anyone in this agency'}
                        </span>
                        <FiChevronDown size={15} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                      </button>
                      <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 4 }}>
                        {scope === 'anyone'
                          ? 'With this link, even agents outside your agency install their own copy'
                          : 'Anyone in your agency with this link can install their own copy'}
                      </div>

                      {scopeMenuOpen && (
                        <div style={dropdownMenuStyle}>
                          <button type="button" onClick={() => { setScope('agency'); setScopeMenuOpen(false); }} style={dropdownItemStyle}>
                            <FiUsers size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                            <div style={{ textAlign: 'left', flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-primary)' }}>Anyone in this agency</div>
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Only people in your agency with the link</div>
                            </div>
                            {scope === 'agency' && <FiCheck size={15} style={{ color: 'var(--accent-purple-deep)', flexShrink: 0 }} />}
                          </button>
                          <button type="button" onClick={() => { setScope('anyone'); setScopeMenuOpen(false); }} style={{ ...dropdownItemStyle, borderBottom: 'none' }}>
                            <FiGlobe size={16} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                            <div style={{ textAlign: 'left', flex: 1 }}>
                              <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--text-primary)' }}>Anyone with the link</div>
                              <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Even agents outside your agency, with the link</div>
                            </div>
                            {scope === 'anyone' && <FiCheck size={15} style={{ color: 'var(--accent-purple-deep)', flexShrink: 0 }} />}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => setView('menu')} disabled={creatingLink}>
                    Cancel
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleCreateLink} disabled={creatingLink}>
                    {creatingLink ? <><FiRefreshCw size={12} className="ca-fp-spin" /> Creating…</> : 'Create link'}
                  </button>
                </div>
              </>
            )}

            {linkResult && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)',
                  }}>
                    {scope === 'anyone' ? <FiGlobe size={17} /> : <FiUsers size={17} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text-primary)' }}>
                      {scope === 'anyone' ? 'Anyone with the link' : 'Anyone in this agency'}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 }}>can install their own copy</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input readOnly value={shareUrl(linkResult.token)} className="ca-fp-editor__textarea"
                    style={{ minHeight: 'auto', height: 38, padding: '8px 12px', fontSize: 12, flex: 1 }}
                    onClick={e => (e.target as HTMLInputElement).select()} />
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={() => handleCopy(linkResult.token)}>
                    {copied ? 'Copied!' : 'Copy link'}
                  </button>
                </div>
                <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                  Edited the agent since sharing this? <strong>Refresh</strong> updates the link's content in
                  place — same URL, nothing breaks for people who already have it.
                </p>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" disabled={linkBusy} onClick={handleRefresh}>
                    {linkBusy ? 'Working…' : refreshed ? 'Refreshed!' : 'Refresh'}
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" disabled={linkBusy} onClick={handleRegenerate}>
                    {linkBusy ? 'Working…' : 'Regenerate'}
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--danger-ghost" disabled={linkBusy} onClick={handleRevoke}>
                    {linkBusy ? 'Working…' : 'Revoke'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const menuRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px',
  background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
  borderRadius: 11, cursor: 'pointer', textAlign: 'left', width: '100%',
};
const menuRowTitleStyle: React.CSSProperties = { fontSize: 13.5, fontWeight: 650, color: 'var(--text-primary)' };
const menuRowSubStyle: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 2 };

const dropdownBtnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%',
  padding: '8px 12px', borderRadius: 8, cursor: 'pointer', textAlign: 'left',
  background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
};
const dropdownMenuStyle: React.CSSProperties = {
  position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 100,
  background: 'var(--surface-card, #1a1a1f)', border: '1px solid var(--border-glass)', borderRadius: 10,
  boxShadow: '0 16px 40px rgba(0,0,0,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column',
};
const dropdownItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', width: '100%',
  background: 'none', border: 'none', borderBottom: '1px solid var(--border-glass)', cursor: 'pointer', textAlign: 'left',
};

export default AgentShareMenuModal;
